// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Box, Grid, Paper, SxProps, Typography } from '@mui/material';

// pages
import { FormEditProgramValues } from '../FormEditProgram';

// ui
import { Empty } from '../../../ui/Empty/Empty';

// props
export interface PreviewProps {
	isLoadingProgram: boolean;
	values: FormEditProgramValues;
}

export const Preview = ({ isLoadingProgram, values }: PreviewProps) => {
	// hooks
	const { t } = useTranslation();

	// vars
	let description = '';
	let name = '';
	let urlHero;
	let urlLogo;

	if (!isLoadingProgram && !values.description) {
		description = t('programDescriptionPlaceholder');
	}

	if (!isLoadingProgram && !values.name) {
		name = t('programNamePlaceholder');
	}

	if (values.description) {
		description = values.description;
	}

	if (values.name) {
		name = values.name;
	}

	if (values.hero.length) {
		urlHero = URL.createObjectURL(values.hero[0]);
	}

	if (values.logo.length) {
		urlLogo = URL.createObjectURL(values.logo[0]);
	}

	let content = <></>;

	if (isLoadingProgram) {
		content = (
			<Empty
				hasContent={false}
				hasTitle={false}
				isLoading={isLoadingProgram}
				LoadingProps={{
					CircularProgressProps: {
						color: 'inherit',
					},
				}}
				position="absolute"
			/>
		);
	} else {
		content = (
			<>
				{urlHero && (
					<>
						<Box
							alt={t('preview')}
							component="img"
							src={urlHero}
							sx={(theme) => {
								return {
									position: 'absolute',
									top: 0,
									left: 0,
									objectFit: 'cover',
									width: '100%',
									height: '100%',
								};
							}}
						/>
						<Box
							sx={(theme) => {
								return {
									background: `linear-gradient(to top, #000, transparent)`,
									position: 'absolute',
									bottom: 0,
									left: 0,
									width: '100%',
									height: 200,
								};
							}}
						/>
					</>
				)}
				<Grid
					justifyContent="space-between"
					container
					direction="column"
					spacing={2}
					sx={{
						minHeight: 300,
						height: 'calc(100% + 16px)',
						position: 'relative',
					}}>
					<Grid alignSelf="flex-end" item>
						<Box
							sx={(theme) => {
								return {
									backgroundColor: theme.palette.common.white,
									borderRadius: 1,
									padding: theme.spacing(1),
									margin: theme.spacing(3),
									width: {
										xs: 60,
										lg: 90,
									},
									height: {
										xs: 60,
										lg: 90,
									},
								};
							}}>
							<Box
								sx={(theme) => {
									let sx: SxProps = {
										borderColor: theme.palette.divider,
										borderStyle: 'solid',
										borderWidth: 1,
										borderRadius: 1,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										width: '100%',
										height: '100%',
										color: theme.palette.brand.grey[500],
										overflow: 'hidden',
									};

									if (!values.logo) {
										sx = {
											...sx,
											borderStyle: 'dashed',
										};
									}

									return sx;
								}}>
								{urlLogo ? (
									<Box
										alt={t('preview')}
										component="img"
										src={urlLogo}
										sx={(theme) => {
											return {
												objectFit: 'contain',
												width: '100%',
												height: '100%',
											};
										}}
									/>
								) : (
									<Typography fontWeight={600} variant="caption">
										{t('logo')}
									</Typography>
								)}
							</Box>
						</Box>
					</Grid>
					<Grid item>
						<Box
							sx={(theme) => {
								return {
									padding: theme.spacing(3),
								};
							}}>
							<Typography variant="h1">{name}</Typography>
							<Typography sx={{ wordBreak: 'break-word' }}>{description}</Typography>
						</Box>
					</Grid>
				</Grid>
			</>
		);
	}

	return (
		<Paper
			sx={(theme) => {
				return {
					padding: theme.spacing(2),
					height: '100%',
				};
			}}>
			<Grid
				alignItems="stretch"
				container
				direction="column"
				spacing={2}
				sx={{ height: 'calc(100% + 16px)' }}>
				<Grid item>
					<Typography variant="h3">{t('preview')}</Typography>
				</Grid>
				<Grid flex={1} item>
					<Box
						sx={(theme) => {
							return {
								backgroundColor: theme.palette.brand.blue[800],
								borderRadius: 1,
								position: 'relative',
								height: '100%',
								color: theme.palette.common.white,
								overflow: 'hidden',
							};
						}}>
						{content}
					</Box>
				</Grid>
			</Grid>
		</Paper>
	);
};
